import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SpringChain } from './SpringChain';
import type { DogBody } from './Body';
import type { FootPose } from './Gait';
import type { DogProfile } from './Profile';

/**
 * The skinned dog: a GLB from `tools/build-dog.mjs` (see the RIG CONTRACT there) driven
 * entirely by the behaviour's numbers — no animation clips.
 * - Feet: two-bone IK per leg on the gait's foot targets, so paws plant where the gait says.
 * - Sit / lie: the pelvis drops and pitches, the feet get posture targets, IK folds the legs.
 * - Ears and tail: SpringChains simulate in world space; their points are mapped onto the
 *   chain bones every frame.
 * - Look-at is split between the neck and head bones; breathing scales the chest bone.
 */
const EYE = 0x3d2814; // warm brown iris; the pupil is the dark part
const PELVIS_Y = 0.47; // rest height of the pelvis bone
const SIT = { y: 0.16, pitch: -0.62 };
const LIE = { y: 0.19, pitch: -0.02 };

interface Leg {
  upper: THREE.Bone;
  lower: THREE.Bone;
  foot: THREE.Bone;
  l1: number;
  l2: number;
  r1: THREE.Vector3; // rest direction of the upper bone (root space)
  r2: THREE.Vector3;
  pole: THREE.Vector3; // which way the middle joint bends
  rest: THREE.Vector3; // rest position of the IK effector (the wrist / hock), root space
  sit: THREE.Vector3;
  lie: THREE.Vector3;
  target: THREE.Vector3;
}

interface BoneChain {
  chain: SpringChain;
  bones: THREE.Bone[]; // driven bones, base first (the leaf is not driven)
  rest: THREE.Vector3[]; // rest direction of each driven bone, root space
}

const _a = new THREE.Vector3();
const _d = new THREE.Vector3();
const _perp = new THREE.Vector3();
const _u = new THREE.Vector3();
const _k = new THREE.Vector3();
const _l = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _pq = new THREE.Quaternion();
const _rootInv = new THREE.Quaternion();
const _identity = new THREE.Quaternion();
const _t = new THREE.Vector3();

export class RiggedBody implements DogBody {
  readonly root = new THREE.Group();
  readonly neckParent: THREE.Object3D;
  readonly neckPos = new THREE.Vector3();
  readonly mouth: THREE.Object3D;
  readonly hat = new THREE.Group();
  readonly collar = new THREE.Group();
  readonly showsCollar = true;
  readonly collarRadius = 0.125;
  readonly ears: SpringChain[] = [];
  readonly tail: SpringChain;

  private pelvis: THREE.Bone;
  private chest: THREE.Bone;
  private neck: THREE.Bone;
  private head: THREE.Bone;
  private legChains: Leg[] = [];
  private chains: BoneChain[] = [];
  private eyeGroups: THREE.Group[] = [];
  private coat: THREE.MeshStandardMaterial | null = null;
  private coatBase = new THREE.Color();

  static load(scene: THREE.Object3D, profile: DogProfile): Promise<RiggedBody> {
    return new Promise((resolve, reject) => {
      new GLTFLoader().load(
        `${import.meta.env.BASE_URL}models/${profile.model}.glb`,
        (gltf) => resolve(new RiggedBody(scene, profile, gltf.scene)),
        undefined,
        reject,
      );
    });
  }

  private constructor(
    private scene: THREE.Object3D,
    profile: DogProfile,
    model: THREE.Group,
  ) {
    this.root.scale.setScalar(profile.size);
    this.root.add(model);
    const bone = (name: string): THREE.Bone => {
      const b = model.getObjectByName(name);
      if (!b) throw new Error(`dog model: missing bone ${name}`);
      return b as THREE.Bone;
    };
    const node = (name: string): THREE.Object3D => {
      const n = model.getObjectByName(name);
      if (!n) throw new Error(`dog model: missing node ${name}`);
      return n;
    };
    this.pelvis = bone('pelvis');
    this.chest = bone('chest');
    this.neck = bone('neck');
    this.head = bone('head');
    this.neckParent = this.chest;
    this.neckPos.copy(this.neck.position);
    this.mouth = node('mouth');
    node('hat').add(this.hat);
    const collarNode = node('collar');
    collarNode.add(this.collar);
    // ring axis along the neck
    this.collar.rotation.x = -Math.atan2(this.head.position.y, this.head.position.z);

    model.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) {
        const m = o as THREE.SkinnedMesh;
        m.castShadow = true;
        m.frustumCulled = false; // IK and lying stretch beyond the rest bounds
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        for (const mat of mats as THREE.MeshStandardMaterial[]) {
          if (mat.name === 'coat') {
            mat.color.set(profile.coat);
            this.coat = mat;
            this.coatBase.set(profile.coat);
          } else if (mat.name === 'mask') mat.color.set(profile.mask);
          else if (mat.name === 'ears') mat.color.set(profile.ears);
          mat.roughness = mat.name === 'nose' ? 0.35 : 0.9;
        }
      }
    });

    // eyes: glossy balls on the head empties, rotated by gaze, squashed to blink
    const wet = new THREE.MeshStandardMaterial({ color: EYE, roughness: 0.15 });
    const pupilMat = new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.3 });
    for (const side of ['L', 'R']) {
      const eye = new THREE.Group();
      eye.add(new THREE.Mesh(new THREE.SphereGeometry(0.03, 14, 12), wet));
      const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.014, 8, 8), pupilMat);
      pupil.position.z = 0.024;
      eye.add(pupil);
      const socket = node(`eye_${side}`);
      socket.rotation.y = (side === 'L' ? -1 : 1) * 0.5; // eyes look out and forward, like a dog's
      socket.add(eye);
      this.eyeGroups.push(eye);
    }

    // legs: FL, FR, BL, BR
    const leg = (upper: string, lower: string, foot: string, front: boolean, sit: THREE.Vector3, lie: THREE.Vector3): Leg => {
      const u = bone(upper);
      const l = bone(lower);
      const f = bone(foot);
      const r1 = l.position.clone().normalize();
      const r2 = f.position.clone().normalize();
      const rest = this.restPosition(f);
      return {
        upper: u,
        lower: l,
        foot: f,
        l1: l.position.length(),
        l2: f.position.length(),
        r1,
        r2,
        pole: new THREE.Vector3(0, 0, front ? -1 : 1),
        rest,
        sit: rest.clone().add(sit),
        lie: rest.clone().add(lie),
        target: rest.clone(),
      };
    };
    this.legChains.push(
      leg('shoulder_L', 'elbow_L', 'wrist_L', true, new THREE.Vector3(0, 0, 0.03), new THREE.Vector3(0, -0.03, 0.22)),
      leg('shoulder_R', 'elbow_R', 'wrist_R', true, new THREE.Vector3(0, 0, 0.03), new THREE.Vector3(0, -0.03, 0.22)),
      leg('hip_L', 'knee_L', 'hock_L', false, new THREE.Vector3(-0.03, -0.02, 0.17), new THREE.Vector3(-0.05, -0.03, 0.22)),
      leg('hip_R', 'knee_R', 'hock_R', false, new THREE.Vector3(0.03, -0.02, 0.17), new THREE.Vector3(0.05, -0.03, 0.22)),
    );

    // tail and ears: spring chains on the anchor empties, mapped onto the bones
    const curl = profile.tailStyle === 'curl';
    this.tail = this.boneChain(node('tail_anchor'), 'tail', profile.size, {
      stiffness: curl ? 180 : 320,
      damping: curl ? 6 : 8,
      gravity: curl ? 2.5 : 1.5,
    });
    const drop = profile.earStyle === 'drop';
    for (const side of ['L', 'R']) {
      this.ears.push(
        this.boneChain(node(`ear_${side}_anchor`), `ear_${side}`, profile.size, {
          stiffness: drop ? 160 : 420,
          damping: drop ? 8 : 10,
          gravity: drop ? 3 : 1.5,
        }),
      );
    }

    scene.add(this.root);
  }

  /** rest position of a bone in root space (walks the translations up to the pelvis) */
  private restPosition(b: THREE.Object3D): THREE.Vector3 {
    const p = new THREE.Vector3();
    for (let o: THREE.Object3D | null = b; o && o.parent !== this.root; o = o.parent) p.add(o.position);
    return p;
  }

  private boneChain(anchor: THREE.Object3D, prefix: string, size: number, opts: { stiffness: number; damping: number; gravity: number }): SpringChain {
    const bones: THREE.Bone[] = [];
    for (let i = 0; ; i++) {
      const b = this.root.getObjectByName(`${prefix}_${i}`);
      if (!b) break;
      bones.push(b as THREE.Bone);
    }
    const driven = bones.slice(0, -1);
    const rest = driven.map((_, i) => bones[i + 1].position.clone().normalize());
    const segLen = bones[1].position.length();
    const chain = new SpringChain(anchor, this.scene, {
      segments: driven.length,
      length: segLen * driven.length * size,
      radius: 0,
      color: 0,
      restDir: rest[0],
      restDirs: rest,
      render: false,
      ...opts,
    });
    this.chains.push({ chain, bones: driven, rest });
    return chain;
  }

  legs(feet: FootPose[], sit: number, lie: number, shakeLift: number, kick: number): void {
    for (let i = 0; i < 4; i++) {
      const L = this.legChains[i];
      const f = feet[i];
      const t = L.target;
      t.copy(L.rest);
      t.y += f.lift;
      t.z += f.z;
      t.lerp(L.sit, sit);
      _t.copy(L.lie);
      t.lerp(_t, lie);
      if (i === 1 && shakeLift > 0) {
        t.y += 0.2 * shakeLift;
        t.z += 0.18 * shakeLift;
      }
      if (i === 2 && kick > 0) {
        t.y += 0.12 * kick;
        t.z -= 0.15 * kick;
      }
      if (i === 3 && kick < 0) {
        t.y -= 0.12 * kick;
        t.z += 0.15 * kick;
      }
    }
  }

  torso(bob: number, pitch: number, sit: number, lie: number): void {
    let y = PELVIS_Y + bob;
    let p = pitch;
    y += (SIT.y - y) * sit;
    p += (SIT.pitch - p) * sit;
    y += (LIE.y - y) * lie;
    p += (LIE.pitch - p) * lie;
    this.pelvis.position.y = y;
    this.pelvis.rotation.x = p;
  }

  breathe(b: number): void {
    const sx = 1 + b * 0.035;
    const sz = 1 + b * 0.02;
    this.chest.scale.set(sx, 1, sz);
    // the ribs swell, not the neck and legs hanging off them
    for (const c of this.chest.children) c.scale.set(1 / sx, 1, 1 / sz);
  }

  look(q: THREE.Quaternion): void {
    this.neck.quaternion.copy(_identity).slerp(q, 0.45);
    this.head.quaternion.copy(_identity).slerp(q, 0.55);
  }

  eyes(yaw: number, pitch: number, lid: number): void {
    for (const eye of this.eyeGroups) {
      eye.rotation.set(pitch, yaw, 0, 'YXZ');
      eye.scale.y = lid;
    }
  }

  setCoat(dirt: number, wet = 0): void {
    if (!this.coat) return;
    this.coat.color.copy(this.coatBase).lerp(new THREE.Color(0x6b5a44), dirt * 0.55).multiplyScalar(1 - 0.35 * wet);
    this.coat.roughness = Math.min(1, 0.9 + dirt * 0.1) - 0.55 * wet;
  }

  finish(dt: number): void {
    this.root.updateMatrixWorld(true);
    this.root.getWorldQuaternion(_rootInv).invert();
    for (const L of this.legChains) this.solveLeg(L);
    // chains read the anchors (head / pelvis), which are final by now
    this.tail.update(dt);
    for (const e of this.ears) e.update(dt);
    for (const c of this.chains) this.applyChain(c);
  }

  /** Two-bone IK in root space: upper bone toward the pole, lower to the target, foot flat. */
  private solveLeg(L: Leg): void {
    L.upper.getWorldPosition(_a);
    this.root.worldToLocal(_a);
    _d.subVectors(L.target, _a);
    const reach = L.l1 + L.l2;
    let d = _d.length();
    if (d < 1e-4) return;
    _d.divideScalar(d);
    d = THREE.MathUtils.clamp(d, Math.abs(L.l1 - L.l2) + 0.005, reach - 0.002);
    const cosA = THREE.MathUtils.clamp((L.l1 * L.l1 + d * d - L.l2 * L.l2) / (2 * L.l1 * d), -1, 1);
    const sinA = Math.sqrt(1 - cosA * cosA);
    _perp.copy(L.pole).addScaledVector(_d, -L.pole.dot(_d));
    if (_perp.lengthSq() < 1e-6) _perp.set(0, 0, L.pole.z);
    _perp.normalize();
    _u.copy(_d).multiplyScalar(cosA).addScaledVector(_perp, sinA); // upper bone direction
    _k.copy(_a).addScaledVector(_u, L.l1); // middle joint
    _l.copy(_a).addScaledVector(_d, d).sub(_k).normalize(); // lower bone direction

    // parent orientation relative to the root
    L.upper.parent!.getWorldQuaternion(_pq);
    _pq.premultiply(_rootInv);
    _q.setFromUnitVectors(L.r1, _u); // upper, root space
    L.upper.quaternion.copy(_pq).invert().multiply(_q);
    _q2.setFromUnitVectors(L.r2, _l); // lower, root space
    L.lower.quaternion.copy(_q).invert().multiply(_q2);
    L.foot.quaternion.copy(_q2).invert(); // paw stays flat to the ground
  }

  /** Point each chain bone along its simulated segment. */
  private applyChain(c: BoneChain): void {
    const pts = c.chain.points;
    // parent orientation of the first bone, relative to the root
    c.bones[0].parent!.getWorldQuaternion(_pq);
    _pq.premultiply(_rootInv);
    let prev = c.chain.origin;
    for (let i = 0; i < c.bones.length; i++) {
      _d.subVectors(pts[i], prev).applyQuaternion(_rootInv).normalize();
      _q.setFromUnitVectors(c.rest[i], _d); // root space
      c.bones[i].quaternion.copy(_pq).invert().multiply(_q);
      _pq.copy(_q);
      prev = pts[i];
    }
  }

  dispose(): void {
    this.scene.remove(this.root);
    this.tail.dispose();
    for (const e of this.ears) e.dispose();
  }
}
